//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import SensorTypeModel from './sensorType.model';

/**
 * @service SensorTypeService
 * @description Service for managing Sensor Type definitions within the aquaculture system.
 * Sensor Types categorize the various hardware sensors used for environmental monitoring.
 *
 * Examples of Sensor Types might include:
 * - **Optical DO Sensor**: For measuring Dissolved Oxygen using optical technology.
 * - **Electrochemical pH Sensor**: For measuring acidity/alkalinity.
 * - **Temperature Probe**: For measuring water temperature.
 *
 * This classification helps in:
 * 1. **Inventory Management**: Tracking specific types of equipment.
 * 2. **Data Interpretation**: Different sensor types might have different calibration needs or data ranges.
 * 3. **Configuration**: Associating specific parameters (QPs) with compatible sensor types.
 *
 * The service interacts with the `/aqu/sensortype/` backend endpoint.
 *
 * @feature 1. Retrieve all defined Sensor Types.
 * @feature 2. Support for large datasets (pageSize=2000) to ensure all types are loaded.
 * @feature 3. Robust management of concurrent HTTP requests to prevent memory leaks.
 *
 * @architecture
 * Extends base Service<SensorTypeModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /aqu/sensortype/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class SensorTypeService extends Service<SensorTypeModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/aqu/sensortype/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/sensortype/');
  }

  /**
   * @method getAllSensorTypes
   * @async
   * @description Fetch all Sensor Type records from the backend.
   *
   * This method performs a GET request to the service endpoint. It is configured to retrieve
   * a large number of records (pageSize=2000) to ensure all types are loaded,
   * ordered by creation date ('new').
   *
   * @returns {Promise<Array<SensorTypeModel>>} Promise resolving to an array of SensorTypeModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * sensorTypeService.getAllSensorTypes().then(types => {
   *   console.log('Sensor Types:', types);
   * });
   */
  public getAllSensorTypes() {
    return new Promise<Array<SensorTypeModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<SensorTypeModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=2000&order=new`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
